import { ApiService } from '../ApiService';
import type { 
  FiscalYear, 
  AuditorCredentials, 
  AuditValidation, 
  CloseFiscalYearResult, 
  CloseFiscalYearParams,
  TransitionCheckResult,
  CreateNextFiscalYearParams,
  TransitionCreateResult
} from '../../../types/fiscal-year';

/**
 * Données pour la création d'un exercice fiscal
 */
export interface CreateFiscalYearRequest {
  startDate: string;
  endDate: string;
  code: string;
}

/**
 * Données pour l'import d'exercices fiscaux
 */
export interface ImportFiscalYearsRequest {
  fiscalYears: CreateFiscalYearRequest[];
}

export const fiscalYearsApi = {
  /**
   * Récupérer tous les exercices fiscaux
   * @see GET /fiscal-years
   */
  getAll: () => 
    ApiService.get<FiscalYear[]>('/fiscal-years'),

  /**
   * Récupérer un exercice fiscal par ID
   * @see GET /fiscal-years/:id
   */
  getById: (id: string) => 
    ApiService.get<FiscalYear>(`/fiscal-years/${id}`),

  /**
   * Créer un nouvel exercice fiscal
   * @see POST /fiscal-years
   */
  create: (data: CreateFiscalYearRequest) => 
    ApiService.post<FiscalYear>('/fiscal-years', data),

  /**
   * Mettre à jour un exercice fiscal (seul le status peut être modifié)
   * @see PUT /fiscal-years/:id
   */
  update: (id: string, data: { status: 'open' | 'closed' }) => 
    ApiService.put<{ success: boolean; data: FiscalYear }>(`/fiscal-years/${id}`, data),

  /**
   * Clôturer un exercice fiscal
   * @see POST /fiscal-years/:id/close
   * 
   * Vérifications effectuées:
   * - Toutes les écritures doivent être postées ou annulées
   * - La balance doit être équilibrée (total débits = total crédits)
   * - Aucune écriture en brouillon ou en attente
   * 
   * @param id - ID de l'exercice fiscal
   * @param params - Paramètres optionnels (force: boolean)
   */
  close: (id: string, params?: CloseFiscalYearParams) => 
    ApiService.post<CloseFiscalYearResult>(`/fiscal-years/${id}/close`, params || {}),

  /**
   * Réouvrir un exercice fiscal clôturé
   * @see POST /fiscal-years/:id/reopen
   */
  reopen: (id: string) => 
    ApiService.post<FiscalYear>(`/fiscal-years/${id}/reopen`, {}),

  /**
   * Auditer un exercice fiscal
   * @see POST /fiscal-years/:id/audit
   * 
   * Pré-requis:
   * - L'exercice doit être clôturé (status: 'closed')
   * - L'auditeur doit avoir des credentials valides
   */
  audit: (id: string, credentials: AuditorCredentials) => 
    ApiService.post<AuditValidation>(`/fiscal-years/${id}/audit`, credentials),

  /**
   * Importer des exercices fiscaux (format JSON)
   * @see POST /fiscal-years/import
   * Note: L'import multipart/form-data (CSV, Excel) est prévu pour une version future
   */
  import: (data: ImportFiscalYearsRequest) => 
    ApiService.post<{ success: boolean; data: { imported: number } }>('/fiscal-years/import', data),

  /**
   * Import de fichier (méthode alternative pour quand le backend supportera les fichiers)
   * @deprecated Utiliser la méthode import avec JSON pour l'instant
   */
  importFile: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return ApiService.post<{ success: boolean; data: { imported: number } }>('/fiscal-years/import', formData);
  },

  // ====== TRANSITION ENTRE EXERCICES FISCAUX ======

  /**
   * Vérifier si une transition vers un nouvel exercice est possible
   * @see GET /fiscal-years/transition/check/:previousFiscalYearId
   * 
   * Vérifie:
   * - L'exercice précédent est clôturé (CLOSED ou AUDITED)
   * - L'exercice est audité (recommandé mais pas obligatoire)
   * - Aucune écriture en brouillon
   * - La balance est équilibrée
   */
  checkTransition: (previousFiscalYearId: string) => 
    ApiService.get<TransitionCheckResult>(`/fiscal-years/transition/check/${previousFiscalYearId}`),

  /**
   * Créer un nouvel exercice fiscal avec report automatique des soldes
   * @see POST /fiscal-years/transition/create-next
   * 
   * Génère automatiquement:
   * - Les écritures de clôture (Classes 6, 7 → Compte 13)
   * - Les écritures d'ouverture (Report classes 1-5)
   * 
   * @param params.code - Code du nouvel exercice (ex: "FY2025")
   * @param params.startDate - Date de début (YYYY-MM-DD)
   * @param params.endDate - Date de fin (YYYY-MM-DD)
   * @param params.businessUnitId - ID de l'unité commerciale (optionnel)
   * @param params.forceWithoutAudit - Forcer sans audit (déconseillé)
   * @param params.forceWithoutClose - Forcer sans clôture (déconseillé)
   */
  createNextWithTransition: (params: CreateNextFiscalYearParams) => 
    ApiService.post<TransitionCreateResult>('/fiscal-years/transition/create-next', params)
};